import { forwardRef, type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import User, { UserGroup } from "admin/types/interfaces/user";
import { AlertCircle, Check, ChevronRight, Globe, Loader2, Search, Users, X } from "lucide-react";
import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import { useDebounce } from "~/hooks/useDebounce";
import { cn } from "~/lib/utils";
import useGroups from "~/pages/admin/hooks/useGroups";
import useUsers from "~/pages/admin/hooks/useUsers";
import { EVERYONE_ID, isEveryoneId } from "~/services/principals";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { getAvatarInitials } from "~/utils/strings";
import { AccessDetailsView, buildExpandedAccess } from "./AccessDetailsView";
import {
  type AccessLevelOption,
  type ShareCollaborators,
  type ShareGroup,
  type ShareUser,
} from "./types";

export type { AccessLevelOption, ShareCollaborators, ShareGroup, ShareUser };

// Surface the backend's message (e.g. the "keep at least one owner" 409) rather
// than the JSON-stringified body an ApiError carries verbatim.
const shareErrorMessage = (e: unknown, fallback: string): string => {
  if (isApiError(e)) return getApiErrorMessage(e);
  return e instanceof Error ? e.message : fallback;
};

const MIN_QUERY_LENGTH = 2;

// The suggestion popover shows a short scrollable list, so a bounded page is all
// it can display. Narrowing the query is the way to reach someone further down.
const SEARCH_RESULT_LIMIT = 50;

type InviteChip =
  | {
      id: string;
      type: "user";
      label: string;
      avatarUrl?: string;
      initials: string;
      metadata: User;
    }
  | {
      id: string;
      type: "group";
      label: string;
      initials: string;
      metadata: UserGroup;
    };

interface UserSuggestion {
  id: string;
  type: "user";
  label: string;
  sub?: string;
  avatarUrl?: string;
  metadata: User;
}

interface GroupSuggestion {
  id: string;
  type: "group";
  label: string;
  metadata: UserGroup;
}

type Suggestion = UserSuggestion | GroupSuggestion;

export interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceLabel: string;
  resourceName?: string;
  collaborators: ShareCollaborators | undefined;
  collaboratorsLoading?: boolean;
  collaboratorsError?: unknown;
  accessLevels: Record<string, AccessLevelOption>;
  defaultAccessLevel: string;
  onShare: (
    recipient: { user?: ShareUser; group?: ShareGroup },
    accessLevel: string | null
  ) => Promise<void>;
  currentUserEmail?: string | null;
  // Whether this resource supports the org-wide `user:*` grant. Off by default:
  // datasets share through the catalog API, which does not accept the wildcard,
  // so only agents and projects surface the General access control.
  allowGeneralAccess?: boolean;
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-0.5 mt-3.5">
    <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
      {children}
    </span>
  </div>
);

const REMOVE_VALUE = "__remove__";

const SelectItemWithDesc = forwardRef<
  HTMLDivElement,
  { value: string; label: string; description?: string; className?: string }
>(({ value, label, description, className }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    value={value}
    className={cn(
      "flex w-full cursor-pointer select-none flex-col rounded-sm px-2 py-1.5 outline-none focus:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
  >
    <div className="flex items-center gap-1.5">
      <span className="flex size-3.5 shrink-0 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-3.5" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>
        <span className="text-sm font-medium leading-none">{label}</span>
      </SelectPrimitive.ItemText>
    </div>
    {description && (
      <span className="mt-0.5 pl-5 text-xs text-muted-foreground">{description}</span>
    )}
  </SelectPrimitive.Item>
));
SelectItemWithDesc.displayName = "SelectItemWithDesc";

interface MemberRowProps {
  id: string;
  label: string;
  sub?: string;
  accessLevel: string | null;
  isGroup: boolean;
  isCurrentUser?: boolean;
  isOwnerLocked?: boolean;
  imageUrl?: string;
  accessLevels: Record<string, AccessLevelOption>;
  onChangeRole: (id: string, role: string) => void;
  onRemove: (id: string) => void;
}

const MemberRow = ({
  id,
  label,
  sub,
  accessLevel,
  isGroup,
  isCurrentUser,
  isOwnerLocked,
  imageUrl,
  accessLevels,
  onChangeRole,
  onRemove,
}: MemberRowProps) => {
  const disabled = isCurrentUser || isOwnerLocked;

  return (
    <div className="flex items-center gap-3 py-2">
      {isGroup ? (
        <span className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-muted">
          <Users className="size-[19px] text-muted-foreground" />
        </span>
      ) : (
        <Avatar className="size-[34px] shrink-0">
          <AvatarImage src={imageUrl || undefined} />
          <AvatarFallback className="text-xs">{label.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{label}</span>
        </div>
        {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
      </div>

      <Select
        value={accessLevel ?? ""}
        onValueChange={(val) => {
          if (val === REMOVE_VALUE) {
            onRemove(id);
          } else {
            onChangeRole(id, val);
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger size="sm" className="w-26 shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-[1300] min-w-[220px]">
          {Object.entries(accessLevels).map(([key, opt]) => (
            <SelectItemWithDesc
              key={key}
              value={key}
              label={opt.label}
              description={opt.description}
            />
          ))}
          <SelectSeparator />
          <SelectItemWithDesc
            value={REMOVE_VALUE}
            label="Remove"
            className="text-destructive focus:text-destructive"
          />
        </SelectContent>
      </Select>
    </div>
  );
};

interface SearchChipsFieldProps {
  chips: InviteChip[];
  onAddChip: (chip: InviteChip) => void;
  onRemoveChip: (id: string) => void;
  inviteRole: string;
  onInviteRoleChange: (role: string) => void;
  accessLevels: Record<string, AccessLevelOption>;
  excludedEmails: string[];
  excludedGroupNames: string[];
  currentUserEmail?: string | null;
}

// TODO: build this on `~/components/ui/command` instead of the inline combobox
// below. That component is a local imitation of shadcn's Command that omits the
// cmdk keyboard/ARIA engine (no arrow navigation, no listbox role), so it cannot
// back this field today; migrating it to cmdk would fix its other consumers too.
const SearchChipsField = ({
  chips,
  onAddChip,
  onRemoveChip,
  inviteRole,
  onInviteRoleChange,
  accessLevels,
  excludedEmails,
  excludedGroupNames,
  currentUserEmail,
}: SearchChipsFieldProps) => {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [roleSelectOpen, setRoleSelectOpen] = useState(false);
  // Tracked by identity rather than index so the highlight survives the list
  // being replaced by a new query's results, and self-heals when the row is gone.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query.trim());
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const enabled = debouncedQuery.length >= MIN_QUERY_LENGTH;

  // `keepPreviousData` holds the previous matches on screen while the next query
  // resolves, so refining a search doesn't flash the list back to a spinner.
  const { data: users = [], isLoading: usersLoading } = useUsers(
    {
      searchQuery: debouncedQuery,
      enabled,
      activeOnly: true,
      withAuth0: false,
      limit: SEARCH_RESULT_LIMIT,
    },
    { keepPreviousData: true }
  );
  const { data: groups = [], isLoading: groupsLoading } = useGroups(
    { searchQuery: debouncedQuery, enabled, limit: SEARCH_RESULT_LIMIT },
    { keepPreviousData: true }
  );

  const chipIds = new Set(chips.map((c) => c.id));
  const alreadyAddedIds = new Set([
    ...excludedEmails.map((e) => e.toLowerCase()),
    ...excludedGroupNames,
  ]);

  const suggestions: Suggestion[] = [
    ...users
      .filter((u) => !chipIds.has(u.email))
      .map(
        (u): UserSuggestion => ({
          id: u.email,
          type: "user",
          label: u.full_name || u.email,
          sub: u.full_name ? u.email : undefined,
          avatarUrl: u.image_url,
          metadata: u,
        })
      ),
    ...groups
      .filter((g) => !chipIds.has(g.name))
      .map(
        (g): GroupSuggestion => ({
          id: g.name,
          type: "group",
          label: g.display_name || g.name,
          metadata: g,
        })
      ),
  ];

  // Pair each row with its disabled state once: keyboard navigation has to skip
  // already-added rows, and the list renders them dimmed.
  const options = suggestions.map((s) => ({
    suggestion: s,
    key: `${s.type}:${s.id}`,
    alreadyAdded: alreadyAddedIds.has(s.type === "user" ? s.id.toLowerCase() : s.id),
  }));
  const selectableIndexes = options.flatMap((o, i) => (o.alreadyAdded ? [] : [i]));

  // Falling back to the first selectable row means Enter picks the top match
  // without arrowing, and a stale `activeKey` from a previous query can't stick.
  const storedIndex = options.findIndex((o) => !o.alreadyAdded && o.key === activeKey);
  const activeIndex = storedIndex >= 0 ? storedIndex : (selectableIndexes[0] ?? -1);
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const isLoading = usersLoading || groupsLoading;
  // SWR reports `isLoading` for the new key even while `keepPreviousData` serves the
  // previous query's rows, so the spinner only belongs when nothing is retained.
  const showSearching = isLoading && options.length === 0;
  // Gate the popover on the live length so it tracks typing; fetch stays debounced.
  const trimmedLength = query.trim().length;
  const showSuggestions =
    focused && !roleSelectOpen && enabled && trimmedLength >= MIN_QUERY_LENGTH;
  const showHint =
    focused && !roleSelectOpen && trimmedLength > 0 && trimmedLength < MIN_QUERY_LENGTH;
  const showPopover = showSuggestions || showHint;

  // Keep the keyboard-driven row in view without stealing focus from the input.
  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const moveActive = (delta: number) => {
    if (!selectableIndexes.length) {
      return;
    }
    const position = selectableIndexes.indexOf(activeIndex);
    const next =
      position < 0
        ? delta > 0
          ? selectableIndexes[0]
          : selectableIndexes[selectableIndexes.length - 1]
        : selectableIndexes[
            (position + delta + selectableIndexes.length) % selectableIndexes.length
          ];
    setActiveKey(options[next].key);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Backspace on an empty query pops the last chip, as chip inputs conventionally do.
    if (e.key === "Backspace" && query === "" && chips.length > 0) {
      onRemoveChip(chips[chips.length - 1].id);
      return;
    }
    if (e.key === "Escape" && showPopover) {
      e.preventDefault();
      setFocused(false);
      return;
    }
    if (!showSuggestions) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(options[activeIndex].suggestion);
    }
  };

  const handleSelect = (s: Suggestion) => {
    const chip: InviteChip =
      s.type === "user"
        ? {
            id: s.id,
            type: "user",
            label: s.label,
            avatarUrl: s.avatarUrl,
            initials: getAvatarInitials(s.label),
            metadata: s.metadata,
          }
        : {
            id: s.id,
            type: "group",
            label: s.label,
            initials: getAvatarInitials(s.label),
            metadata: s.metadata,
          };
    onAddChip(chip);
    setQuery("");
    setActiveKey(null);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative"
      // Rows are no longer tabbable (focus stays on the input), so tabbing out of
      // the field has to be what dismisses the popover.
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
    >
      <div
        onClick={() => {
          setFocused(true);
          inputRef.current?.focus();
        }}
        className="flex min-h-9 cursor-text items-stretch rounded-md border border-border bg-background"
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5 p-1.5 pl-2.5">
          <Search className="size-[15px] shrink-0 text-muted-foreground" />

          {chips.map((chip) => (
            <span
              key={chip.id}
              className="inline-flex h-6 max-w-[200px] items-center gap-1.5 rounded-full border border-border bg-muted pl-1 pr-0.5 text-xs font-medium text-foreground"
            >
              {chip.type === "group" ? (
                <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-accent">
                  <Users className="size-3 text-muted-foreground" />
                </span>
              ) : chip.avatarUrl ? (
                <img
                  src={chip.avatarUrl}
                  alt=""
                  className="size-[18px] shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-accent text-[9px] font-semibold text-accent-foreground">
                  {chip.initials}
                </span>
              )}
              <span className="truncate">{chip.label}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveChip(chip.id);
                }}
                className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="size-[10px]" />
              </button>
            </span>
          ))}

          <input
            ref={inputRef}
            role="combobox"
            aria-label="Search for users and groups to invite"
            aria-autocomplete="list"
            aria-expanded={showPopover}
            aria-controls={showSuggestions ? listboxId : undefined}
            aria-activedescendant={
              showSuggestions && activeIndex >= 0 ? optionId(activeIndex) : undefined
            }
            className="min-w-[80px] flex-1 border-none bg-transparent py-0.5 text-xs text-foreground outline-none placeholder:text-muted-foreground"
            placeholder={chips.length === 0 ? "Search by name, email, or group" : ""}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setFocused(true);
            }}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="flex shrink-0 items-center">
          <div className="my-1 w-px self-stretch bg-border" />
          <Select
            value={inviteRole}
            onValueChange={onInviteRoleChange}
            onOpenChange={(open) => {
              setRoleSelectOpen(open);
              if (open) {
                setFocused(false);
              }
            }}
          >
            <SelectTrigger
              className="w-26 border-none px-3 py-0.5 text-xs font-medium shadow-none focus-visible:ring-0"
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[1300] min-w-[220px]">
              {Object.entries(accessLevels).map(([key, opt]) => (
                <SelectItemWithDesc
                  key={key}
                  value={key}
                  label={opt.label}
                  description={opt.description}
                />
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showPopover && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 max-h-[260px] overflow-auto rounded-lg border border-border bg-popover p-1 shadow-lg">
          {showSuggestions &&
            (showSearching ? (
              <div className="flex items-center gap-2 px-2.5 py-3 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                Searching…
              </div>
            ) : options.length === 0 ? (
              <div className="px-2.5 py-3 text-xs text-muted-foreground">No results found</div>
            ) : (
              <div
                ref={listRef}
                id={listboxId}
                role="listbox"
                aria-label="Search results"
                // Dim the retained rows so a refinement still reads as in flight.
                className={cn(isLoading && "opacity-60")}
              >
                {options.map(({ suggestion: s, key, alreadyAdded }, index) => (
                  <div
                    key={key}
                    id={optionId(index)}
                    data-index={index}
                    role="option"
                    aria-selected={index === activeIndex}
                    aria-disabled={alreadyAdded || undefined}
                    // Keep focus on the input so `aria-activedescendant` stays authoritative.
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => !alreadyAdded && setActiveKey(key)}
                    onClick={alreadyAdded ? undefined : () => handleSelect(s)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left",
                      alreadyAdded ? "cursor-default opacity-50" : "cursor-pointer",
                      !alreadyAdded && index === activeIndex && "bg-accent text-accent-foreground"
                    )}
                  >
                    {s.type === "group" ? (
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
                        <Users className="size-[15px] text-muted-foreground" />
                      </span>
                    ) : (
                      <Avatar className="size-7 shrink-0">
                        <AvatarImage src={s.type === "user" ? s.avatarUrl : undefined} />
                        <AvatarFallback className="text-[10px]">
                          {s.label.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-foreground">{s.label}</p>
                      {s.type === "user" && s.sub && (
                        <p className="truncate text-xs text-muted-foreground">{s.sub}</p>
                      )}
                    </div>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {alreadyAdded ? "Already added" : s.type === "group" ? "Group" : null}
                    </span>
                  </div>
                ))}
              </div>
            ))}

          {showHint && (
            <div className="px-2.5 py-3 text-xs text-muted-foreground">
              Type at least {MIN_QUERY_LENGTH} characters to search
            </div>
          )}
        </div>
      )}

      {/* Result counts are otherwise silent: the list appears with no focus change. */}
      <span role="status" aria-live="polite" className="sr-only">
        {showSuggestions && !showSearching
          ? `${options.length} ${options.length === 1 ? "result" : "results"}`
          : ""}
      </span>
    </div>
  );
};

const buildRecipient = (
  chip: InviteChip,
  accessLevel: string
): { user?: ShareUser; group?: ShareGroup } => {
  if (chip.type === "user") {
    return {
      user: {
        email: chip.metadata.email,
        full_name: chip.metadata.full_name,
        image_url: chip.metadata.image_url,
        access_level: accessLevel,
      },
    };
  }
  return {
    group: {
      name: chip.metadata.name,
      display_name: chip.metadata.display_name,
      is_internal: chip.metadata.is_internal,
      access_level: accessLevel,
      users: [],
    },
  };
};

const GENERAL_ACCESS_RESTRICTED = "restricted";
const GENERAL_ACCESS_EVERYONE = "everyone";

interface GeneralAccessSectionProps {
  isEveryone: boolean;
  roleLabel: string;
  resourceLabel: string;
  disabled?: boolean;
  onChange: (everyone: boolean) => void;
}

// Org-wide access, modeled as the `user:*` wildcard grant. It is kept out of the
// invite field because it is a baseline that layers under individual grants
// rather than an additive per-person one, and the wildcard is only ever valid at
// the base (use) level — so the role is fixed, not selectable.
const GeneralAccessSection = ({
  isEveryone,
  roleLabel,
  resourceLabel,
  disabled,
  onChange,
}: GeneralAccessSectionProps) => {
  const resource = resourceLabel.toLowerCase();
  const restrictedDesc = `Only people with access can open this ${resource}.`;
  const everyoneDesc = `Anyone in your organization can use this ${resource}.`;
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-muted">
        <Globe className="size-[19px] text-muted-foreground" />
      </span>
      <div className="min-w-0 flex-1">
        <Select
          value={isEveryone ? GENERAL_ACCESS_EVERYONE : GENERAL_ACCESS_RESTRICTED}
          onValueChange={(v) => onChange(v === GENERAL_ACCESS_EVERYONE)}
          disabled={disabled}
        >
          <SelectTrigger
            aria-label="General access"
            // The negative margin cancels the padding, so the label still lines up
            // with the member names above while the hover surface gets room to breathe.
            className="-mx-2 h-auto w-auto gap-1 border-none px-2 py-1 text-sm font-medium shadow-none focus-visible:ring-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[1300] min-w-[300px]">
            <SelectItemWithDesc
              value={GENERAL_ACCESS_RESTRICTED}
              label="Restricted"
              description={restrictedDesc}
            />
            <SelectItemWithDesc
              value={GENERAL_ACCESS_EVERYONE}
              label="Everyone in your organization"
              description={everyoneDesc}
            />
          </SelectContent>
        </Select>
        <p className="truncate text-xs text-muted-foreground">
          {isEveryone ? everyoneDesc : restrictedDesc}
        </p>
      </div>
      {isEveryone && (
        <span className="shrink-0 pr-1 text-xs font-medium text-muted-foreground">{roleLabel}</span>
      )}
    </div>
  );
};

export const ShareModal = ({
  open,
  onOpenChange,
  resourceLabel,
  resourceName,
  collaborators,
  collaboratorsLoading,
  collaboratorsError,
  accessLevels,
  defaultAccessLevel,
  onShare,
  currentUserEmail,
  allowGeneralAccess = false,
}: ShareModalProps) => {
  const { showSuccess, showError } = useToast();

  const [chips, setChips] = useState<InviteChip[]>([]);
  const [inviteRole, setInviteRole] = useState(defaultAccessLevel);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmRemoval, setConfirmRemoval] = useState<{
    id: string;
    label: string;
    isGroup: boolean;
    user?: ShareUser;
    group?: ShareGroup;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [generalAccessSaving, setGeneralAccessSaving] = useState(false);
  const [view, setView] = useState<"share" | "details">("share");

  const users = collaborators?.users ?? [];
  const groups = collaborators?.groups ?? [];

  // The `user:*` wildcard is surfaced through the General access control, not as
  // an invite recipient or a member row. Only meaningful when the resource opts
  // into org-wide access.
  const everyoneUser = allowGeneralAccess ? users.find((u) => isEveryoneId(u.email)) : undefined;

  const ownerCount = [...users, ...groups].filter((c) => c.access_level === "owner").length;

  const handleAddChip = (chip: InviteChip) => {
    setChips((prev) => [...prev, chip]);
  };

  const handleClose = () => {
    setView("share");
    setChips([]);
    setInviteRole(defaultAccessLevel);
    onOpenChange(false);
  };

  const handleInvite = async () => {
    if (!chips.length) {
      return;
    }
    setIsSaving(true);
    try {
      await Promise.all(chips.map((chip) => onShare(buildRecipient(chip, inviteRole), inviteRole)));
      setChips([]);
      showSuccess("Access updated");
    } catch (e: unknown) {
      showError(shareErrorMessage(e, "Failed to share"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRoleChange = async (
    recipient: { user?: ShareUser; group?: ShareGroup },
    newRole: string
  ) => {
    try {
      await onShare(recipient, newRole);
      showSuccess("Access updated");
    } catch (e: unknown) {
      showError(shareErrorMessage(e, "Failed to update access"));
    }
  };

  // Toggle the org-wide `user:*` grant: share it at the base level to open access
  // to everyone, unshare to return to restricted. Individual grants are untouched.
  const handleGeneralAccessChange = async (everyone: boolean) => {
    setGeneralAccessSaving(true);
    const level = everyone ? defaultAccessLevel : null;
    try {
      await onShare(
        {
          user: {
            email: EVERYONE_ID,
            full_name: "Everyone in your organization",
            access_level: level,
          },
        },
        level
      );
      showSuccess("Access updated");
    } catch (e: unknown) {
      showError(shareErrorMessage(e, "Failed to update access"));
    } finally {
      setGeneralAccessSaving(false);
    }
  };

  const handleRemoveRequest = (
    id: string,
    label: string,
    isGroup: boolean,
    user?: ShareUser,
    group?: ShareGroup
  ) => {
    setConfirmRemoval({ id, label, isGroup, user, group });
  };

  const handleConfirmRemoval = async () => {
    if (!confirmRemoval) {
      return;
    }
    setIsRemoving(true);
    try {
      await onShare({ user: confirmRemoval.user, group: confirmRemoval.group }, null);
      showSuccess(`${confirmRemoval.label} removed`);
      setConfirmRemoval(null);
    } catch (e: unknown) {
      showError(shareErrorMessage(e, "Failed to remove access"));
    } finally {
      setIsRemoving(false);
    }
  };

  const sortedUsers = [...users]
    .filter((u) => !isEveryoneId(u.email))
    .sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email));

  const hasGroups = groups.length > 0;
  const totalUserCount = useMemo(
    () => buildExpandedAccess(users, groups, accessLevels).length,
    [users, groups, accessLevels]
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) {
            handleClose();
          } else {
            onOpenChange(true);
          }
        }}
      >
        <DialogContent className="gap-2 p-0 sm:max-w-[560px]" showCloseButton={false}>
          {view === "details" ? (
            <>
              <DialogTitle className="sr-only">All users with access</DialogTitle>
              <AccessDetailsView
                resourceName={resourceName ?? resourceLabel}
                users={users}
                groups={groups}
                accessLevels={accessLevels}
                onBack={() => setView("share")}
                onClose={() => handleClose()}
              />
            </>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 px-[22px] pb-3 pt-5">
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-semibold leading-tight tracking-tight">
                    Share {resourceLabel}
                  </DialogTitle>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {resourceName && `${resourceName} · `}
                    {totalUserCount} {totalUserCount === 1 ? "user" : "users"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleClose()}
                  className="mt-[-2px] flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                >
                  <X className="size-[18px]" />
                </button>
              </div>

              <div className="relative px-[22px]">
                <SearchChipsField
                  chips={chips}
                  onAddChip={handleAddChip}
                  onRemoveChip={(id) => setChips((prev) => prev.filter((c) => c.id !== id))}
                  inviteRole={inviteRole}
                  onInviteRoleChange={setInviteRole}
                  accessLevels={accessLevels}
                  excludedEmails={users.map((u) => u.email)}
                  excludedGroupNames={groups.map((g) => g.name)}
                  currentUserEmail={currentUserEmail}
                />
              </div>

              <div className="max-h-[360px] overflow-y-auto px-[22px] pb-1.5 pt-0.5">
                {collaboratorsLoading ? (
                  <div className="flex flex-col gap-3 py-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="size-[34px] rounded-full" />
                        <Skeleton className="h-4 flex-1 rounded" />
                        <Skeleton className="h-[30px] w-26 rounded-md" />
                      </div>
                    ))}
                  </div>
                ) : collaboratorsError ? (
                  <Alert variant="destructive" className="mx-1 my-2">
                    <AlertCircle className="size-4" />
                    <AlertDescription>
                      Failed to load access list. Please try again.
                    </AlertDescription>
                  </Alert>
                ) : sortedUsers.length === 0 && groups.length === 0 && !everyoneUser ? (
                  <div className="mx-1 my-4 rounded-lg border border-dashed border-border bg-muted px-4 py-7 text-center">
                    <p className="text-sm font-medium text-foreground">Only you have access</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Add users or groups above to start sharing this {resourceLabel.toLowerCase()}.
                    </p>
                  </div>
                ) : (
                  <>
                    {sortedUsers.length > 0 && (
                      <>
                        <SectionLabel>Users · {sortedUsers.length}</SectionLabel>
                        <div>
                          {sortedUsers.map((user) => {
                            const isSelf =
                              user.email.toLowerCase() === currentUserEmail?.toLowerCase();
                            const isOnlyOwner = ownerCount === 1 && user.access_level === "owner";
                            return (
                              <MemberRow
                                key={user.email}
                                id={user.email}
                                label={user.full_name || user.email}
                                sub={user.full_name ? user.email : undefined}
                                accessLevel={user.access_level}
                                isGroup={false}
                                isCurrentUser={isSelf}
                                isOwnerLocked={isOnlyOwner}
                                imageUrl={user.image_url}
                                accessLevels={accessLevels}
                                onChangeRole={(_, role) => handleRoleChange({ user }, role)}
                                onRemove={() =>
                                  handleRemoveRequest(
                                    user.email,
                                    user.full_name || user.email,
                                    false,
                                    user,
                                    undefined
                                  )
                                }
                              />
                            );
                          })}
                        </div>
                      </>
                    )}

                    {groups.length > 0 && (
                      <>
                        <SectionLabel>Groups · {groups.length}</SectionLabel>
                        <div>
                          {groups.map((group) => {
                            const isOnlyOwner = ownerCount === 1 && group.access_level === "owner";
                            return (
                              <MemberRow
                                key={group.name}
                                id={group.name}
                                label={group.display_name ?? group.name}
                                accessLevel={group.access_level}
                                isGroup={true}
                                isOwnerLocked={isOnlyOwner}
                                accessLevels={accessLevels}
                                onChangeRole={(_, role) => handleRoleChange({ group }, role)}
                                onRemove={() =>
                                  handleRemoveRequest(
                                    group.name,
                                    group.display_name ?? group.name,
                                    true,
                                    undefined,
                                    group
                                  )
                                }
                              />
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {allowGeneralAccess &&
                !collaboratorsLoading &&
                !collaboratorsError &&
                collaborators && (
                  <div className="border-t border-border px-[22px] pb-1.5 pt-1">
                    <SectionLabel>General access</SectionLabel>
                    <GeneralAccessSection
                      isEveryone={!!everyoneUser}
                      roleLabel={
                        accessLevels[everyoneUser?.access_level ?? defaultAccessLevel]?.label ?? ""
                      }
                      resourceLabel={resourceLabel}
                      disabled={generalAccessSaving}
                      onChange={handleGeneralAccessChange}
                    />
                  </div>
                )}

              <div className="flex items-center justify-between border-t border-border px-[22px] pb-4 pt-3">
                <button
                  type="button"
                  onClick={() => setView("details")}
                  className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  View all users with access
                  <ChevronRight className="size-3.5" />
                </button>

                <Button
                  onClick={chips.length > 0 ? handleInvite : handleClose}
                  loading={isSaving}
                  disabled={isSaving}
                  size="sm"
                  className="h-[34px] px-4 text-sm"
                >
                  {chips.length > 0 ? "Share" : "Done"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmRemoval} onOpenChange={(v) => !v && setConfirmRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <strong>{confirmRemoval?.label}</strong> from this{" "}
              {resourceLabel.toLowerCase()}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleConfirmRemoval} loading={isRemoving}>
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
